import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const siteName = 'RideSaathi';
const siteUrl = 'https://ridesaathi.in';
const defaultImage = `${siteUrl}/assets/ride-saathi-main-logo.png`;
const defaultDescription = 'RideSaathi helps taxi drivers access organized ride requirements from authorized sources and verified contributors.';

type RouteSeo = {
  title: string;
  description: string;
  keywords?: string;
  robots?: string;
};

const routeSeo: Record<string, RouteSeo> = {
  '/': {
    title: 'RideSaathi | Verified Taxi Rides for Drivers',
    description: defaultDescription,
    keywords: 'taxi rides, driver rides, ride leads, taxi driver app, RideSaathi, taxi ride app India',
  },
  '/rides': {
    title: 'Live Taxi Rides | RideSaathi',
    description: 'Browse active ride requirements from authorized sources and verified contributors with RideSaathi credits.',
    keywords: 'live taxi rides, ride requests, taxi driver rides, cab rides India, RideSaathi rides',
  },
  '/plans': {
    title: 'Plans and Pricing | RideSaathi',
    description: 'Compare RideSaathi Free, Starter and Pro plans for organized taxi rides and driver ride unlocks.',
    keywords: 'RideSaathi pricing, taxi ride plans, cab driver rides subscription, ride ride credits',
  },
  '/login': {
    title: 'Login | RideSaathi',
    description: 'Login to your RideSaathi driver dashboard.',
    robots: 'noindex, nofollow',
  },
  '/signup': {
    title: 'Create Driver Account | RideSaathi',
    description: 'Create a free RideSaathi driver account and get free monthly ride unlocks.',
  },
  '/about': {
    title: 'About | RideSaathi',
    description: 'Learn how RideSaathi helps taxi drivers and fleet operators organize ride requirements from verified contributors.',
  },
  '/faq': {
    title: 'FAQ | RideSaathi',
    description: 'Common questions about RideSaathi rides, unlock credits, pricing and support.',
  },
  '/contact': {
    title: 'Contact | RideSaathi',
    description: 'Contact RideSaathi support for account, ride quality and payment help.',
  },
  '/privacy': {
    title: 'Privacy Policy | RideSaathi',
    description: 'Read the RideSaathi privacy policy and cookie usage details.',
  },
  '/terms': {
    title: 'Terms & Conditions | RideSaathi',
    description: 'Read the RideSaathi terms and conditions for drivers and fleet operators.',
  },
  '/data-deletion': {
    title: 'Data Deletion / Opt-out | RideSaathi',
    description: 'Request correction, removal, deletion, or opt-out for RideSaathi data and listings.',
  },
  '/refund-policy': {
    title: 'Refund Policy | RideSaathi',
    description: 'Read the RideSaathi refund policy for subscription plans, duplicate payments, and billing issues.',
  },
  '/community-policy': {
    title: 'Community & Lead Posting Policy | RideSaathi',
    description: 'Read RideSaathi community rules for ride submissions, reporting, and removals.',
  },
  '/grievance-officer': {
    title: 'Contact / Grievance Officer | RideSaathi',
    description: 'Contact RideSaathi for privacy, data, billing, account, listing, or grievance requests.',
  },
  '/payment/return': {
    title: 'Payment Status | RideSaathi',
    description: 'Review your RideSaathi payment status.',
    robots: 'noindex, nofollow',
  },
};

const ensureMeta = (selector: string, attr: 'name' | 'property', key: string, content: string) => {
  let tag = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const ensureCanonical = (href: string) => {
  let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

const SeoAndScrollManager = () => {
  const location = useLocation();

  useEffect(() => {
    const pathSeo = routeSeo[location.pathname] || {
      title: `${siteName} | Taxi Rides for Drivers`,
      description: defaultDescription,
      robots: 'noindex, nofollow',
    };
    const normalizedPath = location.pathname === '/'
      ? '/'
      : location.pathname.replace(/\/+$/, '');
    const canonical = `${siteUrl}${normalizedPath}`;
    const robots = pathSeo.robots || 'index, follow';

    document.title = pathSeo.title;
    ensureMeta('meta[name="description"]', 'name', 'description', pathSeo.description);
    ensureMeta('meta[name="keywords"]', 'name', 'keywords', pathSeo.keywords || 'RideSaathi, taxi ride, rides, leads, taxi leads, driver leads');
    ensureMeta('meta[name="robots"]', 'name', 'robots', robots);
    ensureMeta('meta[property="og:title"]', 'property', 'og:title', pathSeo.title);
    ensureMeta('meta[property="og:description"]', 'property', 'og:description', pathSeo.description);
    ensureMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    ensureMeta('meta[property="og:url"]', 'property', 'og:url', canonical);
    ensureMeta('meta[property="og:site_name"]', 'property', 'og:site_name', siteName);
    ensureMeta('meta[property="og:image"]', 'property', 'og:image', defaultImage);
    ensureMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    ensureMeta('meta[name="twitter:title"]', 'name', 'twitter:title', pathSeo.title);
    ensureMeta('meta[name="twitter:description"]', 'name', 'twitter:description', pathSeo.description);
    ensureMeta('meta[name="twitter:image"]', 'name', 'twitter:image', defaultImage);
    ensureCanonical(canonical);
  }, [location.pathname]);

  useEffect(() => {
    if (location.hash) {
      const id = decodeURIComponent(location.hash.slice(1));
      window.setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
      return;
    }

    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname, location.hash]);

  return null;
};

export default SeoAndScrollManager;
